// React and PropTypes
import { LANDING_PATH as FW_LANDING_PATH } from '../../../constants/frameworkAgreementSegments';
import PropTypes from 'prop-types';

// React Router
import { useLocation, Link } from 'react-router-dom';

// UI Components
import { Separator } from '@shadcn/components/ui/separator';
import { Avatar, AvatarFallback } from '@shadcn/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shadcn/components/ui/tooltip';

// Icons
import {
  ListChecks,
  UsersThree,
  UserGear,
  ChatText,
  Book as BookIcon,
  Building,
  HouseSimple,
  Warehouse,
  // SquaresFour,
  SquareHalf,
  FolderStar,
} from '@phosphor-icons/react';

// Utilities
import clsx from 'clsx';

// Assets
// import logo from '../../../assets/astron_logo.png';

// Local imports
import styles from './SideNav.module.css';
import { tokenManager } from '../../../services/tokenManager';

// Brand configuration
import { brandConfig } from '../../../brands';
import { isFeatureEnabled, FEATURES } from '../../../utils/featureFlags';

/**
 * NavItem component serves as a navigation item for the sidebar.
 * It displays a link to the specified route, with an icon and text.
 * The component is styled based on whether the item is active or inactive,
 * enhancing user experience and accessibility.
 */
const NavItem = ({ to = '/', icon: Icon, text = '', activePath = to }) => {
  /**
   * location is the current location of the user.
   * @type {Object}
   */
  const location = useLocation();

  /**
   * isActive is a boolean that determines if the navigation item is active.
   * @type {boolean}
   */
  const isActive =
    to === '/'
      ? location.pathname === '/' ||
        location.pathname.startsWith('/data-management')
      : location.pathname.startsWith(activePath);

  /**
   * Renders the navigation item component.
   * This component displays a link to the specified route,
   * with an icon and text. The component is styled based on
   * whether the item is active or inactive, enhancing
   * user experience and accessibility.
   */
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to}
          className={clsx(
            styles.sideNav__item,
            isActive
              ? styles['sideNav__item--active']
              : styles['sideNav__item--inactive']
          )}
          aria-label={text}
        >
          <Icon className={styles.sideNav__icon} aria-hidden="true" />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * NavItem prop types.
 * @type {Object}
 */
NavItem.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  text: PropTypes.string.isRequired,
  activePath: PropTypes.string,
};

/**
 * NavItem component displayName.
 * @type {string}
 */
NavItem.displayName = 'NavItem';

/**
 * SideNav component serves as the sidebar navigation for the application.
 * It provides links to various sections of the app, including jobs, clients,
 * vendors, inspectors, and agencies. Each navigation item is represented by
 * an icon and text, allowing users to easily navigate through the application.
 * The component also highlights the active navigation item based on the current
 * route, enhancing user experience and accessibility.
 */
const SideNav = () => {
  /**
   * Renders the sidebar navigation component.
   * This component displays the application logo, a separator,
   * a navigation section with various links to different sections
   * of the app, and an avatar for the user.
   */
  return (
    <aside className={styles.sideNav} aria-label="Main Navigation">
      <div className={styles.sideNav__logo} role="banner">
        {/* <img src={logo} alt="Astron Logo" /> */}
        {/* <Link
          to="/"
          aria-label="I.T.EX New Vision - Go to homepage"
          style={{
            color: '#2a6419',
            textAlign: 'center',
            lineHeight: 1.2,
            textDecoration: 'none',
          }}
        >
          <strong style={{ fontStyle: 'italic', display: 'block' }}>
            I.T.EX
          </strong>
          <em style={{ fontSize: '0.75em', fontWeight: 'normal' }}>
            New Vision
          </em>
        </Link> */}
        {brandConfig.logo.type === 'image' && (
          <img src={brandConfig.logo.src} alt={brandConfig.logo.alt} />
        )}

        {brandConfig.logo.type === 'text' && (
          <Link
            to="/"
            style={{
              textAlign: 'center',
              textDecoration: 'none',
              lineHeight: 1.2,
            }}
          >
            <strong style={{ display: 'block', fontStyle: 'italic' }}>
              {brandConfig.logo.primary}
            </strong>
            <em style={{ fontSize: '0.75em', fontWeight: 'normal' }}>
              {brandConfig.logo.secondary}
            </em>
          </Link>
        )}
      </div>

      <div className={styles.sideNav__scroll}>
        <Separator className={styles.sideNav__separator} />

        <TooltipProvider>
          <nav className={styles.sideNav__nav}>
            <NavItem to="/" icon={HouseSimple} text="Home" />
            {isFeatureEnabled(FEATURES.INQUIRIES) && (
              <NavItem to="/inquiries" icon={ChatText} text="Inquiries" />
            )}

            {isFeatureEnabled(FEATURES.FRAMEWORK_AGREEMENTS) && (
              <NavItem
                to={FW_LANDING_PATH}
                activePath="/framework-agreements"
                icon={BookIcon}
                text="Framework Agreements"
              />
            )}

            {isFeatureEnabled(FEATURES.JOBS) && (
              <NavItem to="/jobs" icon={ListChecks} text="Jobs" />
            )}

            {isFeatureEnabled(FEATURES.WORK_ORDERS) && (
              <NavItem to="/work-orders" icon={SquareHalf} text="Work Orders" />
            )}

            {isFeatureEnabled(FEATURES.CLIENTS) && (
              <NavItem to="/clients" icon={UsersThree} text="Clients" />
            )}

            {isFeatureEnabled(FEATURES.VENDORS) && (
              <NavItem to="/vendors" icon={Warehouse} text="Vendors" />
            )}

            {isFeatureEnabled(FEATURES.INSPECTORS) && (
              <NavItem to="/inspectors" icon={UserGear} text="Inspectors" />
            )}

            {isFeatureEnabled(FEATURES.AGENCIES) && (
              <NavItem to="/agencies" icon={Building} text="Agencies" />
            )}

            {isFeatureEnabled(FEATURES.CLIENT_QMS_CERTIFICATES) && (
              <NavItem
                to="/client-certificates"
                icon={FolderStar}
                text="Client Certificates"
              />
            )}
          </nav>
        </TooltipProvider>
      </div>

      <div className={styles.sideNav__avatarArea}>
        {/* <Separator className={styles.sideNav__separator} /> */}
        <Avatar className={styles.sideNav__avatar}>
          <AvatarFallback className={styles.sideNav__avatarFallback}>
            {tokenManager.getUserInitials()}
          </AvatarFallback>
        </Avatar>
      </div>
    </aside>
  );
};

/**
 * SideNav component displayName.
 * @type {string}
 */
SideNav.displayName = 'SideNav';

/**
 * SideNav component export.
 * @type {React.Component}
 */
export default SideNav;
